import { useMemo } from "react";
import { Loader2, RefreshCw } from "lucide-react";
import { AiUsageReport } from "@/types/admin/ai-usage";
import { formatCount, formatPerMTok, formatUsd, PROVIDER_LABELS } from "./constants";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface ModelsTabProps {
  report: AiUsageReport;
  isSyncing: boolean;
  onSyncPrices: () => void;
}

const HeaderCell = ({ children, right }: { children: React.ReactNode; right?: boolean }) => (
  <th className={`px-4 py-2.5 md:px-5 ${right ? "text-right" : ""}`}>{children}</th>
);

const ModelsTab = ({ report, isSyncing, onSyncPrices }: ModelsTabProps) => {
  const usageByFeature = useMemo(() => {
    const totals = new Map<string, { calls: number; costUsd: number }>();
    for (const row of report.byFeature) {
      if (row.source !== "edge") continue;
      const t = totals.get(row.feature) ?? { calls: 0, costUsd: 0 };
      t.calls += row.calls;
      t.costUsd += row.costUsd;
      totals.set(row.feature, t);
    }
    return totals;
  }, [report.byFeature]);

  const workflows = report.byFeature.filter((row) => row.source === "workflow");
  const pricesUpdated = report.pricesUpdatedAt
    ? new Date(report.pricesUpdatedAt).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" })
    : "never";

  return (
    <div className="flex flex-col gap-4">
      <div className="overflow-hidden rounded-2xl border border-[#E5EAF0] bg-white shadow-sm">
        <div className="flex flex-col gap-3 border-b border-[#E5EAF0] px-4 py-3 sm:flex-row sm:items-center sm:justify-between md:px-5">
          <div className="flex items-start gap-2.5">
            <GenieIcon name="brain" size={20} gradientEnabled secondaryColor="#FF4DB8" label="" />
            <div>
              <div className="text-sm font-semibold text-[#222B45]">Edge function features</div>
              <div className="text-xs text-[#64748B]">
                Default models live in <span className="font-mono">supabase/functions/_shared/ai/features.ts</span> · prices
                from OpenRouter, updated {pricesUpdated}
              </div>
            </div>
          </div>
          <button
            onClick={onSyncPrices}
            disabled={isSyncing}
            className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl border border-[#E5EAF0] bg-white px-4 text-sm font-medium text-gray-700 transition-all hover:border-genie-primary/40 hover:text-genie-primary disabled:opacity-60 sm:min-h-[40px]"
          >
            {isSyncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Refresh prices
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E5EAF0] bg-[#F9FAFC] text-left text-xs font-medium uppercase tracking-wide text-[#64748B]">
                <HeaderCell>Feature</HeaderCell>
                <HeaderCell>Model</HeaderCell>
                <HeaderCell right>Input / MTok</HeaderCell>
                <HeaderCell right>Output / MTok</HeaderCell>
                <HeaderCell right>Calls</HeaderCell>
                <HeaderCell right>Cost</HeaderCell>
                <HeaderCell>Function</HeaderCell>
              </tr>
            </thead>
            <tbody>
              {report.registry.map((entry) => {
                const usage = usageByFeature.get(entry.feature);
                return (
                  <tr key={entry.feature} className="border-b border-[#E5EAF0] last:border-0">
                    <td className="px-4 py-3 md:px-5">
                      <div className="font-mono text-[13px] text-[#222B45]">{entry.feature}</div>
                      <div className="text-xs text-[#64748B]">{entry.description}</div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 md:px-5">
                      <div className="font-mono text-xs text-[#222B45]">{entry.model}</div>
                      <div className="text-xs text-[#64748B]">{PROVIDER_LABELS[entry.provider] ?? entry.provider}</div>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-[#222B45] md:px-5">{formatPerMTok(entry.price?.inputPerMTok)}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-[#222B45] md:px-5">{formatPerMTok(entry.price?.outputPerMTok)}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-[#222B45] md:px-5">{usage ? formatCount(usage.calls) : "—"}</td>
                    <td className="px-4 py-3 text-right font-semibold tabular-nums text-[#222B45] md:px-5">
                      {usage ? formatUsd(usage.costUsd) : "—"}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-[#222B45] md:px-5">{entry.edgeFunction}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-[#E5EAF0] bg-white shadow-sm">
        <div className="flex items-start gap-2.5 border-b border-[#E5EAF0] px-4 py-3 md:px-5">
          <GenieIcon name="code" size={20} gradientEnabled secondaryColor="#FF4DB8" label="" />
          <div>
            <div className="text-sm font-semibold text-[#222B45]">GitHub workflows</div>
            <div className="text-xs text-[#64748B]">Models workflows ran in this period — set per workflow by its CLAUDE_MODEL</div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E5EAF0] bg-[#F9FAFC] text-left text-xs font-medium uppercase tracking-wide text-[#64748B]">
                <HeaderCell>Workflow</HeaderCell>
                <HeaderCell>Model</HeaderCell>
                <HeaderCell right>Runs</HeaderCell>
                <HeaderCell right>Cost</HeaderCell>
              </tr>
            </thead>
            <tbody>
              {workflows.map((row) => (
                <tr key={`${row.feature}:${row.model}`} className="border-b border-[#E5EAF0] last:border-0">
                  <td className="px-4 py-3 font-mono text-[13px] text-[#222B45] md:px-5">{row.feature}</td>
                  <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-[#222B45] md:px-5">{row.model}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-[#222B45] md:px-5">{formatCount(row.calls)}</td>
                  <td className="px-4 py-3 text-right font-semibold tabular-nums text-[#222B45] md:px-5">{formatUsd(row.costUsd)}</td>
                </tr>
              ))}
              {workflows.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-10 text-center text-sm text-[#64748B]">
                    No workflow runs reported in this range.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ModelsTab;
